import { usePDFActions, useGetGeneratePDFQuery } from '../../hooks';
import { Button, Checkbox } from '../../ui';
import { Box, CircularProgress, Stack } from '@mui/material';
import * as backendServices from '../../services/backendServices';
import { BusinessDocType } from '../../types/Enums';
import { useSplitScreenDocumentView } from './SplitScreenDocumentViewContext';
import { useEffect, useMemo } from 'react';
import { base64ToBlob } from '../../utils';

function DesktopPDFActions({
    documentId,
    isDraft,
    businessDocType,
    clearAlerts,
    pushAlert,
    generatedPDF,
}: {
    documentId: number;
    isDraft: boolean;
    businessDocType: BusinessDocType;
    clearAlerts: () => void;
    pushAlert: (alert: { type: string; text: string }) => void;
    generatedPDF: backendServices.ViewModels.GeneratePDFUIViewModel | null;
}) {
    const { handleDownloadPDF, handleOpenInNewWindow, isFetchingPDFOpenInNewWindow, isFetchingPDFDownload } =
        usePDFActions({ documentId, isDraft, businessDocType, clearAlerts, pushAlert, generatedPDF });

    return (
        <>
            <Button
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                variant="secondary"
                onClick={handleOpenInNewWindow}
                disabled={isFetchingPDFOpenInNewWindow || !generatedPDF?.dataBlob}
                startIcon={isFetchingPDFOpenInNewWindow && <CircularProgress size="2rem" />}
            >
                Open PDF in New Window
            </Button>
            <Button
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                variant="secondary"
                onClick={handleDownloadPDF}
                disabled={isFetchingPDFDownload || !generatedPDF?.dataBlob}
                startIcon={isFetchingPDFDownload && <CircularProgress size="2rem" />}
            >
                Download PDF
            </Button>
        </>
    );
}

export const SplitScreenControlComponent = ({
    documentId,
    isDraft,
    businessDocType,
    clearAlerts,
    pushAlert,
    documentPDFData = null,
}: {
    documentId: number;
    isDraft: boolean;
    businessDocType: BusinessDocType;
    clearAlerts: () => void;
    pushAlert: (alert: { type: string; text: string }) => void;
    documentPDFData?: any;
}) => {
    let generatedPDF = useGetGeneratePDFQuery({
        documentId,
        isDraft,
        businessDocType,
        enabled: !!documentId,
    });

    // for invoice creation IMA mode, we get the PDF data from the documentCreatorProps
    generatedPDF = useMemo(() => {
        if (!documentId && documentPDFData?.pdfFileData?.FileData) {
            const dataBlob = base64ToBlob(
                documentPDFData.pdfFileData.FileData,
                documentPDFData.pdfFileData.FileContentType
            );
            const fileName = documentPDFData.pdfFileData.FileName;
            return {
                dataBlob,
                fileName,
            };
        } else {
            return generatedPDF;
        }
    }, [documentPDFData, documentId, generatedPDF]);

    const splitScreenDocumentViewContext = useSplitScreenDocumentView();
    const shouldSplitScreenDocumentView = splitScreenDocumentViewContext?.splitScreenDocumentView ?? false;
    const setShouldSplitScreenDocumentView = splitScreenDocumentViewContext?.setSplitScreenDocumentView ?? (() => {});
    const setPDFData = splitScreenDocumentViewContext?.setPDFData;

    useEffect(() => {
        if (generatedPDF && setPDFData) {
            setPDFData(generatedPDF);
        }
    }, [generatedPDF, setPDFData]);

    return (
        <Stack
            direction="row"
            sx={{
                justifyContent: 'right',
                gap: '1em',
            }}
        >
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                <Checkbox
                    label="PDF Split Screen"
                    checked={shouldSplitScreenDocumentView}
                    onChange={() => setShouldSplitScreenDocumentView(!shouldSplitScreenDocumentView)}
                />
                <DesktopPDFActions
                    documentId={documentId}
                    isDraft={isDraft}
                    businessDocType={businessDocType}
                    clearAlerts={clearAlerts}
                    pushAlert={pushAlert}
                    generatedPDF={generatedPDF}
                />
            </Box>
        </Stack>
    );
};
